import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * LoadingState — calm pulsing skeleton, never a spinning loader.
 *
 * Variants:
 *   - "card"   : full-width card with three skeleton rows; matches the
 *                visual footprint of CruxInsight so swap-in feels stable.
 *   - "inline" : a thin row used in forms / smaller surfaces.
 *
 * Props:
 *   - variant : 'card' | 'inline'  (default 'card')
 *   - rows    : number             (skeleton lines for 'card', default 3)
 *   - label   : optional string    spoken to screen readers
 *   - height  : optional override  for inline variant (default 16px)
 */
export default function LoadingState({
  variant = 'card',
  rows = 3,
  label = 'Loading',
  height,
}) {
  if (variant === 'inline') {
    return (
      <div
        role="status"
        aria-label={label}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: fontStack,
        }}
      >
        <span className="crux-skel" style={{ width: 80, height: height || 16, borderRadius: 6 }} />
        <span className="crux-skel" style={{ width: 140, height: height || 16, borderRadius: 6 }} />
        <SkeletonStyles />
      </div>
    );
  }

  return (
    <Card padding={spacing.lg}>
      <div role="status" aria-label={label} style={{ fontFamily: fontStack }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.base }}>
          <span className="crux-skel" style={{ width: 110, height: 12, borderRadius: 4 }} />
          <span className="crux-skel" style={{ width: 80, height: 10, borderRadius: 4 }} />
        </div>

        {/* Body rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: rows }).map((_, i) => (
            <span
              key={i}
              className="crux-skel"
              style={{
                width: i === rows - 1 ? '60%' : '100%',
                height: 14,
                borderRadius: radius.sm,
              }}
            />
          ))}
        </div>
      </div>
      <SkeletonStyles />
    </Card>
  );
}

/* ---- shared keyframes ---- */
function SkeletonStyles() {
  return (
    <style>{`
      @keyframes crux-skel-pulse {
        0%, 100% { opacity: 0.35; }
        50%      { opacity: 0.65; }
      }
      .crux-skel {
        display: inline-block;
        background: ${colors.border};
        animation: crux-skel-pulse 1.6s ease-in-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .crux-skel { animation: none; opacity: 0.5; }
      }
    `}</style>
  );
}
